/**
 * Data-loading hooks shared by the authenticated pages.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';
import { useSession } from './session';

/* -------------------------------------------------------------------------- */
/* Generic async loader                                                        */
/* -------------------------------------------------------------------------- */

export interface AsyncState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Runs `loader` whenever `deps` change. The loader receives a fresh access
 * token so a refreshed Supabase session is picked up automatically.
 */
export function useAsync<T>(
  loader: (token: string | null) => Promise<T>,
  deps: unknown[],
  options: { enabled?: boolean } = {},
): AsyncState<T> {
  const enabled = options.enabled !== false;
  const { getToken, status } = useSession();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [generation, setGeneration] = useState(0);
  const mounted = useRef(true);
  const latest = useRef(loader);
  latest.current = loader;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (status === 'loading') return;
    if (status === 'unconfigured') {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const token = await getToken();
        const result = await latest.current(token);
        if (cancelled || !mounted.current) return;
        setData(result);
        setError(null);
      } catch (caught) {
        if (cancelled || !mounted.current) return;
        setError(
          caught instanceof ApiError
            ? caught
            : new ApiError({ code: 'unknown', status: 0, message: 'Something went wrong.' }),
        );
      } finally {
        if (!cancelled && mounted.current) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, status, generation, ...deps]);

  const reload = useCallback(() => setGeneration((value) => value + 1), []);
  return { data, error, loading, reload };
}

/* -------------------------------------------------------------------------- */
/* Current account                                                             */
/* -------------------------------------------------------------------------- */

export interface MeState {
  me: Awaited<ReturnType<typeof api.me>> | null;
  loading: boolean;
  error: ApiError | null;
  reload: () => void;
}

/**
 * Loads /me, which carries the authoritative plan, quota and entitlements.
 * Refreshes when a diagnosis completes so the quota meter stays accurate.
 */
export function useMe(options: { enabled?: boolean } = {}): MeState {
  const state = useAsync(async (token) => api.me(token), [], options);
  return { me: state.data, loading: state.loading, error: state.error, reload: state.reload };
}

/* -------------------------------------------------------------------------- */
/* Copy to clipboard                                                           */
/* -------------------------------------------------------------------------- */

export function useCopy(): { copied: string | null; copy: (text: string, id?: string) => void } {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = useCallback(async (text: string, id = 'default') => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for browsers without the async clipboard API.
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(id);
      window.setTimeout(() => setCopied((current) => (current === id ? null : current)), 2000);
    } catch {
      setCopied(null);
    }
  }, []);

  return { copied, copy };
}

/** Announce a transient message to assistive technology. */
export function useAnnounce(): { message: string; announce: (message: string) => void } {
  const [message, setMessage] = useState('');
  const announce = useCallback((next: string) => {
    setMessage(next);
    window.setTimeout(() => setMessage(''), 4000);
  }, []);
  return { message, announce };
}
